import { useState, useEffect, Suspense, lazy, type ReactNode } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
// import apiClient from '@/lib/axios';
import { SettingsProvider } from './contexts/SettingsContext';
import { CategoryProvider } from './contexts/CategoryContext';
import Layout from './components/Layout';

const UnifiedLogin = lazy(() => import('./pages/UnifiedLogin'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Users = lazy(() => import('./pages/Users'));
const StoreCollections = lazy(() => import('./pages/StoreCollections'));
const StoreAssignments = lazy(() => import('./pages/StoreAssignments'));
const Collections = lazy(() => import('./pages/Collections'));
const GuoduomeiProject = lazy(() => import('./pages/GuoduomeiProject'));
const Expenses = lazy(() => import('./pages/Expenses'));
const OtherExpenses = lazy(() => import('./pages/OtherExpenses'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Salaries = lazy(() => import('./pages/Salaries'));
const Settings = lazy(() => import('./pages/Settings'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectAccounting = lazy(() => import('./pages/ProjectAccounting'));
const MyCollections = lazy(() => import('./pages/MyCollections'));
const Finance = lazy(() => import('./pages/Finance'));
const StoreIncome = lazy(() => import('./pages/StoreIncome'));
const StoreAssets = lazy(() => import('./pages/StoreAssets'));

const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm">
    加载中...
  </div>
);

const RoutesShell = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<PageLoading />}>{children}</Suspense>
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // 检查本地存储的登录状态
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const handleLoginSuccess = (userData: any, _token: string) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    toast.success('已退出登录');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // build marker 202610031139 (changes chunk hash)
  if (typeof window !== 'undefined') (window as any).__APP_BUILD__ = '202610031139';
  // 未登录状态
  if (!isAuthenticated) {
    return (
      <SettingsProvider>
        <CategoryProvider>
          <RoutesShell><Routes>
            <Route path="/login" element={<UnifiedLogin onLoginSuccess={handleLoginSuccess} />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes></RoutesShell>
        </CategoryProvider>
      </SettingsProvider>
    );
  }

  // 司机角色可以访问门店回收、项目回收、费用报销和查看工资（兼容旧 employee 司机角色）
  if (user?.role === 'driver' || user?.role === 'employee') {
    return (
      <SettingsProvider>
        <CategoryProvider>
          <RoutesShell><Routes>
            <Route path="/" element={<Layout user={user} onLogout={handleLogout} />}>
              <Route index element={<StoreCollections />} />
              <Route path="store-collections" element={<StoreCollections />} />
              <Route path="collections" element={<Collections />} />
              <Route path="project-income" element={<Navigate to="/collections" replace />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="salaries" element={<Salaries />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
          </Routes></RoutesShell>
        </CategoryProvider>
      </SettingsProvider>
    );
  }

  // 京东七鲜查看员：只允许查看京东七鲜项目
  if (user?.role === 'qixian_viewer') {
    return (
      <SettingsProvider>
        <CategoryProvider>
          <RoutesShell><Routes>
            <Route path="/" element={<Layout user={user} onLogout={handleLogout} />}>
              <Route index element={<Navigate to="/store-collections" replace />} />
              <Route path="store-collections" element={<StoreCollections />} />
              <Route path="*" element={<Navigate to="/store-collections" replace />} />
            </Route>
          </Routes></RoutesShell>
        </CategoryProvider>
      </SettingsProvider>
    );
  }

  // 员工角色（staff）只能访问特定页面
  if (user?.role === 'staff') {
    return (
      <SettingsProvider>
        <CategoryProvider>
          <RoutesShell><Routes>
            <Route path="/" element={<Layout user={user} onLogout={handleLogout} />}> 
              <Route index element={<MyCollections />} />
              <Route path="my-collections" element={<MyCollections />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="salaries" element={<Salaries />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
          </Routes></RoutesShell>
        </CategoryProvider>
      </SettingsProvider>
    );
  }

  // 管理员/经理可以访问所有页面
  return (
    <SettingsProvider>
      <CategoryProvider>
        <RoutesShell><Routes>
          <Route path="/" element={<Layout user={user} onLogout={handleLogout} />}>
            <Route index element={<Dashboard />} />
            <Route path="users" element={<Users />} />
            <Route path="store-collections" element={<StoreCollections />} />
            <Route path="store-assignments" element={<StoreAssignments />} />
            <Route path="guoduomei-project" element={<GuoduomeiProject />} />
            <Route path="collections" element={<Navigate to="/guoduomei-project" replace />} />
            <Route path="project-income" element={<Navigate to="/guoduomei-project?tab=income" replace />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="other-expenses" element={<OtherExpenses />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="salaries" element={<Salaries />} />
            <Route path="settings" element={<Settings />} />
            <Route path="projects" element={<Projects />} />
            <Route path="project-accounting/:projectId" element={<ProjectAccounting />} />
            <Route path="finance" element={<Finance />} />
            <Route path="store-income" element={<StoreIncome />} />
            <Route path="store-assets" element={<StoreAssets />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Route>
        </Routes></RoutesShell>
      </CategoryProvider>
    </SettingsProvider>
  );
}

export default App;
